import { queryClientInstance } from "@/api/QueryProvider";
import { supabase, supabaseConfigError } from "@/api/supabaseClient";
import { revokeSession } from "@/auth/revokeSession";
import {
  beginWebSignOut,
  completeWebSignOut,
  discardWebAuthStorage,
  shouldDiscardAuthEvent,
  shouldDiscardSignedOutSession,
  WEB_SIGNOUT_DEADLINE_MS,
} from "@/auth/webSignOutLatch";
import { offlineQueue } from "@/offline/offlineQueue";
import { getOfflineStore } from "@/offline/store";
import { pauseOfflineSync, resumeOfflineSync } from "@/offline/sync/syncPause";
import { resetFinderCaseNotices } from "@/ui/finder/finderCaseNotice";
import { clearFinderModeLock } from "@/ui/finder/finderModeLock";
import type { Session, User } from "@supabase/supabase-js";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Platform } from "react-native";

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  authReady: boolean;
  signOut: () => Promise<void>;
};

function clearOfflineQueue(): Promise<void> {
  return offlineQueue.clear().catch((err: unknown) => {
    console.error(
      "AuthContext: offline queue clear failed",
      err instanceof Error ? err.message : err,
    );
  });
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  authReady: false,
  signOut: async () => {},
});

function withDeadline(work: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    work.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      () => {
        clearTimeout(timer);
        resolve();
      },
    );
  });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  /** Angenommene Web-Sitzung nicht löschen, wenn danach ein verworfenes Ereignis kommt. */
  const preserveAcceptedWebSession = useRef(false);

  useEffect(() => {
    let mounted = true;
    let subscription: { unsubscribe: () => void } | null = null;

    if (supabaseConfigError) {
      setAuthReady(true);
      return () => {
        mounted = false;
      };
    }

    const prevUserIdRef = { current: null as string | null };

    const init = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (!mounted) return;
        if (Platform.OS === "web" && shouldDiscardSignedOutSession(data?.session)) {
          if (!preserveAcceptedWebSession.current) {
            setSession(null);
            setUser(null);
          }
          void discardWebAuthStorage();
          return;
        }
        if (error) {
          console.error("AuthContext: getSession failed", error.message);
          setSession(null);
          setUser(null);
          return;
        }
        if (Platform.OS === "web" && data.session) {
          preserveAcceptedWebSession.current = true;
          resumeOfflineSync();
        }
        setSession(data.session ?? null);
        setUser(data.session?.user ?? null);
        if (prevUserIdRef.current == null && data.session?.user?.id) {
          prevUserIdRef.current = data.session.user.id;
        }
      } catch (err: unknown) {
        console.error(
          "AuthContext: getSession threw",
          err instanceof Error ? err.message : err,
        );
        if (!mounted) return;
        setSession(null);
        setUser(null);
      } finally {
        if (mounted) setAuthReady(true);
      }
    };
    init();

    try {
      const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
        const previousUserId = prevUserIdRef.current;
        if (event === "SIGNED_OUT") {
          resetFinderCaseNotices();
          clearFinderModeLock(previousUserId);
        }
        if (Platform.OS === "web" && shouldDiscardAuthEvent(event, newSession)) {
          if (!preserveAcceptedWebSession.current) {
            setSession(null);
            setUser(null);
          }
          void discardWebAuthStorage();
          return;
        }
        if (Platform.OS === "web" && newSession) {
          preserveAcceptedWebSession.current = true;
          resumeOfflineSync();
        }
        const newUserId = newSession?.user?.id ?? null;
        const hadUser = previousUserId !== null;
        const userIdChanged = newUserId !== previousUserId;
        prevUserIdRef.current = newUserId;
        if (userIdChanged && (hadUser || newUserId === null)) {
          queryClientInstance.clear();
          const clearing = offlineQueue.clear();
          if (event === "SIGNED_OUT") {
            clearing.catch((err: unknown) => {
              console.error(
                "AuthContext: offline queue clear failed",
                err instanceof Error ? err.message : err,
              );
            });
          }
          void getOfflineStore()
            .then((store) => store.clearAll())
            .catch((err: unknown) => {
              console.error(
                "AuthContext: offline store clear failed",
                err instanceof Error ? err.message : err,
              );
            });
        }
        setSession(newSession ?? null);
        setUser(newSession?.user ?? null);
      });
      if (listener?.subscription && typeof listener.subscription.unsubscribe === "function") {
        subscription = listener.subscription;
      }
    } catch (err: unknown) {
      console.error(
        "AuthContext: onAuthStateChange threw",
        err instanceof Error ? err.message : err,
      );
    }

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    resetFinderCaseNotices();
    clearFinderModeLock(session?.user?.id ?? user?.id ?? null);
    // Einziger Web/Nativ-Unterschied beim Aufräumen.
    // Nativ: SIGNED_OUT setzt die Session im Listener schon vor clearAll auf null
    // (wie main); signOut wartet danach noch auf clearAll. Der Storage-Fallback
    // in revokeSession gilt nur auf Web.
    // Web: React-State sofort null, damit das Gate nach /login geht.
    // Sync und laufende Queries stoppen vorher, damit kein getSession
    // parallel refreshed. Während des Latches bleibt dieselbe User-ID
    // draußen, auch mit neuem Refresh-Token. Danach sperren nur Tokens,
    // die dieser Logout gesehen hat. Ein explizites SIGNED_IN oder
    // PASSWORD_RECOVERY setzt User-ID und allowNextSignedIn zurück und
    // behält gesehene Tokens. Schlägt der Server-Logout fehl, bleibt der
    // Refresh-Token serverseitig gültig und wird nur lokal verworfen.
    // Server- und Client-Logout teilen sich eine Frist; danach läuft der
    // Latch noch kurz weiter.
    if (Platform.OS === "web") {
      const accessToken = session?.access_token ?? null;
      pauseOfflineSync();
      void queryClientInstance.cancelQueries().catch((err: unknown) => {
        console.error(
          "AuthContext: signOut threw",
          err instanceof Error ? err.message : err,
        );
      });
      preserveAcceptedWebSession.current = false;
      beginWebSignOut({
        userId: session?.user?.id ?? user?.id ?? null,
        accessToken,
        refreshToken: session?.refresh_token ?? null,
      });
      setSession(null);
      setUser(null);
      queryClientInstance.clear();
      void clearOfflineQueue();
      void getOfflineStore()
        .then((store) => store.clearAll())
        .catch((err: unknown) => {
          console.error(
            "AuthContext: offline store clear failed",
            err instanceof Error ? err.message : err,
          );
        });
      if (typeof supabase.auth.stopAutoRefresh === "function") {
        void Promise.resolve(supabase.auth.stopAutoRefresh()).catch((err: unknown) => {
          console.error(
            "AuthContext: signOut threw",
            err instanceof Error ? err.message : err,
          );
        });
      }
      let serverRevoke: Promise<void> = Promise.resolve();
      if (accessToken && typeof supabase.auth.admin?.signOut === "function") {
        serverRevoke = supabase.auth.admin
          .signOut(accessToken, "global")
          .then(({ error }) => {
            if (error) {
              console.warn("AuthContext: signOut failed", error.message);
            }
          })
          .catch((err: unknown) => {
            console.warn(
              "AuthContext: signOut threw",
              err instanceof Error ? err.message : err,
            );
          });
      }
      const clientDone = revokeSession({
        signOut: (options) => supabase.auth.signOut(options),
        onlyMatchingLatch: true,
      });
      await discardWebAuthStorage();
      await withDeadline(Promise.all([serverRevoke, clientDone]), WEB_SIGNOUT_DEADLINE_MS);
      completeWebSignOut();
      await discardWebAuthStorage();
      return;
    }

    try {
      await revokeSession({
        signOut: (options) => supabase.auth.signOut(options),
      });
    } finally {
      queryClientInstance.clear();
      await clearOfflineQueue();
      await (await getOfflineStore()).clearAll();
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, authReady, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
